/* eslint-disable import/prefer-default-export */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Slider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DetailsIcon from '@mui/icons-material/Details';
import PanoramaVerticalIcon from '@mui/icons-material/PanoramaVertical';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import NumericInput from 'react-numeric-input';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@mui/styles';
import { AllTShirtMeasuresMandatory } from '../../types/databaseTables/idealteemeasures';
import { GlobalContext } from '../../Context/GlobalContext/GlobalContextProvider';
import { UserContext } from '../../Context/UserContext/UserContextProvider';
import { BodyTypeLabels, FitTypeLabels } from '../../types/contexts/userContext';
import { LoggingContext } from '../../Context/LoggingContext/LoggingContext';

function DialogActionBar({
  dialogPage,
  setDialogPage,
  handleClose,
  showClose,
  initialLoad,
}: {
  dialogPage: number;
  setDialogPage: React.Dispatch<React.SetStateAction<number>>;
  handleClose: () => void;
  showClose: boolean;
  initialLoad: boolean;
}) {
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <DialogActions
      sx={{
        display: 'grid',
        gridTemplateColumns: `1fr 6fr 1fr`,
        gap: '1px 1px',
        padding: 0,
        height: 60,
      }}
    >
      {/* Empty Box */}
      <Box sx={{ width: '100%' }} />
      {/* Middle Box */}
      <Box sx={{ textAlign: 'center' }}>
        <Box>
          <Slider
            sx={{
              width: '100px',
              '& .MuiSlider-thumb': {
                height: 24,
                width: 24,
              },
              '& .MuiSlider-track': { height: 8, borderRadius: 4 },
              '& .MuiSlider-rail': { height: 8, borderRadius: 4 },
            }}
            min={1}
            max={initialLoad ? 5 : 3}
            step={1}
            defaultValue={1}
            value={dialogPage + 1}
            onChange={(_event, newValue: number | number[]) => {
              setDialogPage((newValue as number) - 1);
              addLog(`Generator page slider toggled to ${dialogPage}`, location.pathname);
            }}
          />
        </Box>
      </Box>
      {/* Right Box */}
      <Box>
        {showClose && (
          <Button
            color='primary'
            autoFocus
            onClick={() => {
              addLog(`Generator page close button clicked`, location.pathname);
              handleClose();
            }}
          >
            Close
          </Button>
        )}
      </Box>
    </DialogActions>
  );
}

interface NumberSetStateProps {
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  min: number;
  max: number;
}

const useStyles = makeStyles(() => ({
  dialogContent: {
    height: 380,
    width: 330,
    alignSelf: 'center',
    marginTop: 32,
  },
}));

const bodyTypeButtons: Record<
  BodyTypeLabels,
  {
    label: string;
    icon: JSX.Element;
  }
> = {
  ectomorph: { label: 'ectomorph (slim)', icon: <PanoramaVerticalIcon /> },
  regular: { label: 'regular', icon: <CropPortraitIcon /> },
  endomorph: { label: 'endomorph (round)', icon: <RadioButtonUncheckedIcon /> },
  mesomorph: { label: 'mesomorph (athletic)', icon: <DetailsIcon /> },
};

function BodyTypeDialog(props: {
  setBodyType: React.Dispatch<React.SetStateAction<BodyTypeLabels>>;
  setDialogPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const classes = useStyles();
  const { setBodyType, setDialogPage } = props;
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <>
      <DialogTitle id='responsive-dialog-title'>Select Your Body Type:</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box
          sx={{
            justifyContent: 'center',
            display: 'grid',
            gridTemplateColumns: '150px 150px',
            gridTemplateRows: '150px 150px',
            gap: '5px 5px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
        >
          {Object.keys(bodyTypeButtons).map((typeString) => (
            <Button
              sx={{
                '& .MuiButton-label': {
                  display: 'grid',
                  gridTemplateRows: '50px 50px',
                  gap: '10px 10px',
                  '& .MuiButton-startIcon': {
                    justifyContent: 'center',
                    '& svg': {
                      fontSize: 50,
                    },
                  },
                },
              }}
              key={typeString}
              color='primary'
              variant='contained'
              startIcon={bodyTypeButtons[typeString as BodyTypeLabels].icon}
              size='large'
              onClick={() => {
                setBodyType(typeString as BodyTypeLabels);
                setDialogPage((prev) => prev + 1);
                addLog(`Generator page body type selected: ${typeString}`, location.pathname);
              }}
            >
              {bodyTypeButtons[typeString as BodyTypeLabels].label}
            </Button>
          ))}
        </Box>
      </DialogContent>
    </>
  );
}

const fitOptions = ['tight', 'regular', 'loose'];

function FitDialog(props: {
  setFitType: React.Dispatch<React.SetStateAction<FitTypeLabels>>;
  setDialogPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const classes = useStyles();
  const { setFitType, setDialogPage } = props;
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <>
      <DialogTitle id='responsive-dialog-title'>Select Your Preferred Fit:</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box
          sx={{
            justifyContent: 'center',
            display: 'grid',
            gridTemplateColumns: '300px',
            gridTemplateRows: '100px 100px 100px',
            gap: '5px 5px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
        >
          {fitOptions.map((fitString) => (
            <Button
              key={fitString}
              color='primary'
              variant='contained'
              size='large'
              onClick={() => {
                setFitType(fitString as FitTypeLabels);
                setDialogPage((prev) => prev + 1);
                addLog(`Generator page fit type selected: ${fitString}`, location.pathname);
              }}
            >
              {fitString}
            </Button>
          ))}
        </Box>
      </DialogContent>
    </>
  );
}

interface HeightAndWeightDialogProps {
  height: number;
  weight: number;
  setHeight: React.Dispatch<React.SetStateAction<number>>;
  setWeight: React.Dispatch<React.SetStateAction<number>>;
  onSubmit: () => void;
  initialLoad: boolean;
}

function HeightSlider(props: NumberSetStateProps) {
  const { value, setValue, min, max } = props;
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs>
          <Slider
            value={value}
            onChange={(_event, newValue: number | number[]) => {
              addLog(`Generator page: height or weight slider toggled`, location.pathname);
              setValue(newValue as number);
            }}
            aria-labelledby='continuous-slider'
            min={min}
            max={max}
          />
        </Grid>
        <Grid item>
          <div style={{ display: 'flex' }}>
            <NumericInput
              mobile
              className='form-control'
              step={1}
              min={min}
              max={max}
              size={1}
              value={value}
              onChange={(newValue: number | null) =>{ 
                addLog(`Generator page: Height or weight numeric input changed.`, location.pathname)
                setValue(Number(newValue))}}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

function HeightAndWeightDialog(props: HeightAndWeightDialogProps) {
  const classes = useStyles();
  const { height, weight, setHeight, setWeight, onSubmit, initialLoad } = props;
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <>
      <DialogTitle id='responsive-dialog-title'>Update your height and weight</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box
          sx={{
            justifyContent: 'center',
            display: 'grid',
            gridTemplateColumns: '300px',
            gridTemplateRows: '140px 110px 50px',
            gap: '5px 5px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
            '& .MuiButton-root': {
              width: '100%',
            },
          }}
        >
          <div>
            <DialogContentText>
              Height: {height}cm ({Math.floor(height / 2.54 / 12)}&apos;{' '}
              {Math.round((height / 2.54) % 12)}&quot;)
            </DialogContentText>
            <HeightSlider value={height} setValue={setHeight} min={140} max={220} />
            <DialogContentText>
              Weight: {weight}kg ({Math.round(weight * 2.205)}lbs)
            </DialogContentText>
            <HeightSlider value={weight} setValue={setWeight} min={40} max={180} />
          </div>
          <Box
            sx={{
              '& p': {
                margin: 0,
                lineHeight: 1.2,
                '&:first-of-type': {
                  marginBottom: '8px',
                },
              },
            }}
          >
            <DialogContentText>
              Warning: this will overwrite your existing measurements.
            </DialogContentText>
            <DialogContentText>
              (you can always revert to previous measurements using the measurement log below the
              measurement sliders)
            </DialogContentText>
          </Box>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
            <Button
              color='primary'
              variant='contained'
              size='large'
              onClick={() => {
                addLog(`Generator page height and weight submit button clicked`, location.pathname);
                onSubmit();
              }}
            >
              {initialLoad ? 'Calculate' : 'Submit'}
            </Button>
          </div>
        </Box>
      </DialogContent>
    </>
  );
}

function checkMeasurements(
  height: number,
  weight: number,
  bodyType: BodyTypeLabels,
  fitType: FitTypeLabels
): { errorMessagesArray: (string | boolean)[]; errorScript: string } {
  let errorScript = '';

  const heightCheck =
    typeof height === 'number' && height >= 140 && height <= 220
      ? true
      : 'height is not a number between 140 and 220';
  const weightCheck =
    typeof weight === 'number' && weight >= 50 && weight <= 180
      ? true
      : 'weight is not a number between 50 and 180';
  const bodyTypeCheck = ['ectomorph', 'regular', 'mesomorph', 'endomorph'].includes(bodyType)
    ? true
    : `bodyType is not a valid option. Option provided: ${bodyType}`;
  const fitTypeCheck = ['tight', 'regular', 'loose'].includes(fitType)
    ? true
    : `fitType is not a valid option. Option provided: ${fitType}`;
  const errorMessagesArray = [heightCheck, weightCheck, bodyTypeCheck, fitTypeCheck].filter(
    (x) => !x === true
  );
  errorScript = errorMessagesArray.join();
  return { errorScript, errorMessagesArray };
}

function WelcomeSection({
  setDialogPage,
}: {
  setDialogPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const classes = useStyles();
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <>
      <DialogTitle id='responsive-dialog-title'>Welcome</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <p>Hi there! This is a tool to help people buy t-shirts that actually fit.</p>
        <p>
          The basic premise is that I measure t-shirts and then compare them to your ideal t-shirt
          measurements.
        </p>
        <p>In order to get started, we need to calculate your ideal t-shirt measurements.</p>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            color='primary'
            variant='contained'
            size='large'
            onClick={() => {
              setDialogPage((prev) => prev + 1);
              addLog(`Generator page 'Calculate Measurements' button clicked.`, location.pathname);
            }}
            sx={{ width: '100%' }}
          >
            Calculate Measurements
          </Button>
        </Box>
      </DialogContent>
    </>
  );
}

function GeneratorEndPage({ handleClose }: { handleClose: () => void }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { addLog } = useContext(LoggingContext);
  const location = useLocation()

  return (
    <>
      <DialogTitle id='responsive-dialog-title'>Success!</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <p>That&apos;s it! We now have your ideal t-shirt measurements</p>
        <p>
          All of the measured t-shirts that we have in the database will now be sorted in order of
          those that fit you best.
        </p>
        <p>
          You can also filter t-shirts by specific measurements, brands/ manufacturers, color, etc.
        </p>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            color='primary'
            variant='outlined'
            size='large'
            onClick={() => {
              addLog(`Generator page: 'search the database' clicked `, location.pathname);

              handleClose();
            }}
            sx={{ width: '100%' }}
          >
            Search the database
          </Button>
        </Box>
        <br />
        <p>
          Alternatively, you can head over to the Ideal Measurements page in order to
          customise/adjust your ideal measurements.
        </p>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            size='large'
            onClick={() => {
              addLog(`Generator page: 'go to ideal measurements' button clicked`, location.pathname);
              navigate('/idealmeasurements');
              handleClose();
            }}
            sx={{ width: '100%' }}
          >
            Go to your ideal measurements
          </Button>
        </Box>
      </DialogContent>
    </>
  );
}

export function IdealMeasuresGenerator() {
  const location = useLocation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  // USECONTEXTS
  const { fetchPost } = useContext(GlobalContext);
  const {
    userDetailsState,
    dispatchUserDetailsState,
    openIdealMeasuresGeneratorState,
    setOpenIdealMeasuresGeneratorState,
  } = useContext(UserContext);
  // USESTATES
  const [bodyType, setBodyType] = useState<BodyTypeLabels>('regular');
  const [fitType, setFitType] = useState<FitTypeLabels>('regular');
  const [dialogPage, setDialogPage] = useState(0);
  const [height, setHeight] = useState(180);
  const [weight, setWeight] = useState(70);
  const [initialLoad, setInitialLoad] = useState(location.pathname !== '/idealmeasurements');
  const { enqueueSnackbar } = useSnackbar();
  const { addLog } = useContext(LoggingContext);

  useEffect(() => {
    if (location.pathname !== '/idealmeasurements') {
      if (initialLoad === false) {
        setInitialLoad(true);
      }
      if (userDetailsState.idealGarmentMeasurements.chest === 0) {
        // If there are no ideal measurements saved, then redirect to home page
        navigate('/');
      }
    }
    if (location.pathname === '/idealmeasurements') {
      if (initialLoad === true) {
        setInitialLoad(false);
      }
    }
  }, [location.pathname]);

  const idealTeeMeasurements = userDetailsState.idealGarmentMeasurements;

  const handleClose = React.useCallback(() => {
    if (userDetailsState.idealGarmentMeasurements.chest === 0) {
      enqueueSnackbar(`Please update the measures generator to use the site's features`, {
        variant: 'warning',
      });
      addLog(`Generator page: premature attempt to close`, location.pathname);
      return;
    }
    setDialogPage(0);
    setOpenIdealMeasuresGeneratorState(false);
  }, [userDetailsState.idealGarmentMeasurements]);

  const onSubmit = React.useCallback(async () => {
    const { errorScript, errorMessagesArray } = checkMeasurements(
      height,
      weight,
      bodyType,
      fitType
    );
    const descriptionOfFunction = 'attempt to calculate user measurements';
    if (errorMessagesArray.length > 0) {
      enqueueSnackbar(`Error trying ${descriptionOfFunction} ${errorScript}`, { variant: 'error' });
    }

    try {
      // Send new measurements to back end if logged in
      const response = (await fetchPost('/measurements/user/calculateusermeasurementsnologin', {
        height,
        weight,
        fitType,
        bodyType,
        idealTeeMeasurements,
      })) as unknown as {
        'db response': {
          idealteemeasuresObject: AllTShirtMeasuresMandatory;
        };
      };
      if (Object.keys(response || {}).length === 0) {
        // If no response then send error
        enqueueSnackbar('Error updating calculated user measurements. No Response.', {
          variant: 'error',
        });
      } else {
        const { idealteemeasuresObject } = response['db response'];
        dispatchUserDetailsState({
          payload: {
            idealGarmentMeasurements: idealteemeasuresObject,
            height,
            weight,
            fitType,
            bodyType,
          },
          type: 'update from generator',
        });
        enqueueSnackbar('Measurements Updated.', { variant: 'success' });
      }
      if (initialLoad) {
        setDialogPage((prev) => prev + 1);
        return;
      }
      setDialogPage(0);
      setOpenIdealMeasuresGeneratorState(false);
    } catch (error) {
      enqueueSnackbar(`Error trying ${descriptionOfFunction} ${errorScript}`, { variant: 'error' });
    }
  }, [height, weight, fitType, bodyType, idealTeeMeasurements]);

  const defaultPages = {
    0: <BodyTypeDialog setDialogPage={setDialogPage} setBodyType={setBodyType} />,
    1: <FitDialog setFitType={setFitType} setDialogPage={setDialogPage} />,
    2: (
      <HeightAndWeightDialog
        height={height}
        setHeight={setHeight}
        weight={weight}
        setWeight={setWeight}
        onSubmit={onSubmit}
        initialLoad={initialLoad}
      />
    ),
  };

  const intialLoadPages = {
    0: <WelcomeSection setDialogPage={setDialogPage} />,
    1: <BodyTypeDialog setDialogPage={setDialogPage} setBodyType={setBodyType} />,
    2: <FitDialog setFitType={setFitType} setDialogPage={setDialogPage} />,
    3: (
      <HeightAndWeightDialog
        height={height}
        setHeight={setHeight}
        weight={weight}
        setWeight={setWeight}
        onSubmit={onSubmit}
        initialLoad={initialLoad}
      />
    ),
    4: <GeneratorEndPage handleClose={handleClose} />,
  };

  const dialogPages = initialLoad ? intialLoadPages : defaultPages;

  return (
    <div>
      <Dialog
        fullScreen={fullScreen}
        maxWidth='xs'
        open={openIdealMeasuresGeneratorState}
        onClose={handleClose}
        aria-labelledby='responsive-dialog-title'
      >
        {dialogPages[dialogPage as keyof typeof dialogPages]}
        <DialogActionBar
          dialogPage={dialogPage}
          setDialogPage={setDialogPage}
          handleClose={handleClose}
          showClose={(userDetailsState.idealGarmentMeasurements.chest || 0) > 0}
          initialLoad={initialLoad}
        />
      </Dialog>
    </div>
  );
}
